import React, { useEffect, useRef, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import type { Listing } from '../lib/types';

interface ListingsMapProps {
  listings: Listing[];
  onListingClick?: (listing: Listing) => void;
  selectedListing?: Listing | null;
}

export function ListingsMap({ listings, onListingClick, selectedListing }: ListingsMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [markers, setMarkers] = useState<google.maps.Marker[]>([]);
  const [clusterer, setClusterer] = useState<MarkerClusterer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initMap = async () => {
      try {
        setLoading(true);
        const loader = new Loader({
          apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY!,
          version: 'weekly',
        });

        const { Map } = await loader.importLibrary('maps');
        
        // Center on Brooklyn
        const center = { lat: 40.6782, lng: -73.9442 };
        
        if (mapRef.current) {
          const mapInstance = new Map(mapRef.current, {
            center,
            zoom: 12,
            styles: [
              {
                featureType: 'poi',
                elementType: 'labels',
                stylers: [{ visibility: 'off' }]
              }
            ],
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false
          });

          setMap(mapInstance);

          // Initialize MarkerClusterer
          const newClusterer = new MarkerClusterer({
            map: mapInstance,
            markers: [],
          });
          setClusterer(newClusterer);
        }
      } catch (err) {
        console.error('Error initializing map:', err);
        setError('Failed to load map. Please check your internet connection and try again.');
      } finally {
        setLoading(false);
      }
    };

    initMap();
  }, []);

  useEffect(() => {
    if (!map || !clusterer) return;

    // Clear existing markers
    markers.forEach(marker => marker.setMap(null));
    clusterer.clearMarkers();

    // Create new markers
    const newMarkers = listings.map(listing => {
      if (!listing.coordinates || !listing.coordinates.coordinates) {
        return null;
      }
      
      const position = {
        lat: listing.coordinates.coordinates[1],
        lng: listing.coordinates.coordinates[0]
      };

      const marker = new google.maps.Marker({
        position,
        map,
        title: listing.title,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: selectedListing?.id === listing.id ? '#4F46E5' : '#6366F1',
          fillOpacity: 0.7,
          strokeColor: 'white',
          strokeWeight: 2,
        }
      });

      marker.addListener('click', () => {
        onListingClick?.(listing);
      });

      return marker;
    }).filter(Boolean) as google.maps.Marker[];

    setMarkers(newMarkers);
    clusterer.addMarkers(newMarkers);

    // If a listing is selected, center and zoom to it
    if (selectedListing && selectedListing.coordinates && selectedListing.coordinates.coordinates) {
      const position = {
        lat: selectedListing.coordinates.coordinates[1],
        lng: selectedListing.coordinates.coordinates[0]
      };
      map.panTo(position);
      map.setZoom(15);
    }
  }, [map, clusterer, listings, selectedListing, onListingClick]);

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-lg">
        <Loader className="w-8 h-8 animate-spin text-primary-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-lg">
        <div className="text-center p-4">
          <p className="text-red-500 mb-2">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-primary-500 text-white rounded-md"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={mapRef} className="w-full h-full rounded-lg" />
  );
}